import { getFirestore, collection, addDoc, query, where, orderBy, limit, getDocs, updateDoc, deleteDoc, doc, writeBatch, serverTimestamp, onSnapshot } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

export const InAppNotificationService = {
    /**
     * Create a notification
     * targetUid: Recipient UID
     * type: 'follow' | 'like' | 'comment' | 'guestbook'
     * message: Text to display
     * fromUser: Sender User Object (uid, displayName, photoURL)
     * link: Target URL (e.g. /user/xyz or /series/123)
     */
    createNotification: async (targetUid, type, message, fromUser, link) => {
        // Prevent Self-Notification (unless system)
        if (!targetUid) return;
        if (type !== 'system' && (!fromUser || targetUid === fromUser.uid)) return;

        try {
            const payload = {
                type,
                message,
                targetLink: link,
                isRead: false,
                createdAt: serverTimestamp()
            };

            if (fromUser) {
                payload.fromUid = fromUser.uid;
                payload.fromSnapshot = {
                    displayName: fromUser.displayName || fromUser.email?.split('@')[0] || 'Anonymous',
                    photoURL: fromUser.photoURL || null
                };
            } else {
                // System Notification
                payload.fromUid = 'system';
                payload.fromSnapshot = {
                    displayName: 'Mirinae System',
                    photoURL: '/icon-192.png' // Use app icon
                };
            }

            await addDoc(collection(db, `users/${targetUid}/notifications`), payload);
        } catch (error) {
            console.error("InAppNotificationService.createNotification Error:", error);
            // Non-blocking error
        }
    },

    /**
     * Get recent notifications
     */
    getNotifications: async (uid, limitCount = 20) => {
        try {
            const q = query(
                collection(db, `users/${uid}/notifications`),
                orderBy("createdAt", "desc"),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("InAppNotificationService.getNotifications Error:", error);
            return [];
        }
    },

    /**
     * Mark single notification as read
     */
    markAsRead: async (uid, notifId) => {
        try {
            const ref = doc(db, `users/${uid}/notifications`, notifId);
            await updateDoc(ref, { isRead: true });
        } catch (error) {
            console.error("InAppNotificationService.markAsRead Error:", error);
        }
    },

    /**
     * Mark ALL notifications as read
     */
    markAllAsRead: async (uid) => {
        try {
            const q = query(
                collection(db, `users/${uid}/notifications`),
                where("isRead", "==", false),
                limit(500) // Batch limit
            );
            const snapshot = await getDocs(q);
            if (snapshot.empty) return;

            const batch = writeBatch(db);
            snapshot.docs.forEach(d => {
                batch.update(d.ref, { isRead: true });
            });
            await batch.commit();
        } catch (error) {
            console.error("InAppNotificationService.markAllAsRead Error:", error);
        }
    },

    /**
     * Subscribe to unread count (For Badge)
     */
    subscribeUnreadCount: (uid, callback) => {
        const q = query(
            collection(db, `users/${uid}/notifications`),
            where("isRead", "==", false)
        );
        return onSnapshot(q, (snapshot) => {
            callback(snapshot.size);
        });
    },

    /**
     * Delete a notification (Cleanup)
     */
    deleteNotification: async (uid, notifId) => {
        try {
            await deleteDoc(doc(db, `users/${uid}/notifications`, notifId));
        } catch (error) {
            console.error("InAppNotificationService.deleteNotification Error:", error);
        }
    }
};
